import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import HomologacionSINFields from '../common/HomologacionSINFields';
// AddServiceModal.tsx — Co Marca — Crear Servicio
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Building2, Tag } from 'lucide-react';
import { createServicio } from '../../services/servicioService';
import { fetchCategories } from '../../services/categoryService';
import { fetchSucursales } from '../../services/pos/sucursalService';
import ImageUpload from '../Upload/ImageUpload';

interface AddServiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onServiceAdded: () => void;
}

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accentDark:     '#ea8406',
};

const css = `

.asm-modal * { font-family:'DM Sans',sans-serif; box-sizing:border-box; }
@keyframes asmFadeIn  { from{opacity:0;} to{opacity:1;} }
@keyframes asmSlideUp { from{opacity:0;transform:translateY(24px) scale(.98);} to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes spin { to{transform:rotate(360deg);} }
.asm-overlay { animation:asmFadeIn .22s ease both; }
.asm-dialog  { animation:asmSlideUp .32s cubic-bezier(.16,1,.3,1) both; }
.asm-input {
    width:100%; padding:10px 14px; border-radius:10px;
    border:1.5px solid var(--ad-line, #e5e7eb); background:#fafafa;
    font-size:.88rem; color:var(--ad-ink, #374151); outline:none; transition:all .2s;
}
.asm-input:focus { border-color:${colors.primary}; box-shadow:0 0 0 3px color-mix(in srgb, ${colors.primary} 7.06%, transparent); background:var(--ad-surface, #fff); }
.asm-select { appearance:none; }
.asm-textarea { resize:vertical; min-height:72px; }
.asm-btn-primary {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff; border:none; border-radius:11px;
    padding:10px 22px; font-size:.88rem; font-weight:800;
    cursor:pointer; box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.28);
    transition:all .22s cubic-bezier(.16,1,.3,1);
}
.asm-btn-primary:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.34); }
.asm-btn-primary:disabled { background:var(--ad-line, #e2e8f0); color:var(--ad-muted, #94a3b8); box-shadow:none; cursor:not-allowed; }
.asm-btn-ghost {
    display:inline-flex; align-items:center; gap:7px;
    background:var(--ad-soft, #f8fafc); border:1.5px solid var(--ad-line, #e2e8f0);
    color:var(--ad-ink, #374151); border-radius:11px;
    padding:10px 18px; font-size:.88rem; font-weight:700;
    cursor:pointer; transition:all .2s;
}
.asm-btn-ghost:hover { background:var(--ad-line, #f1f5f9); }
.asm-suc-item { transition:background .15s; border-radius:9px; }
.asm-suc-item:hover { background:#f0f9ff; }
.custom-scroll::-webkit-scrollbar { width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background:rgba(97, 37, 123,.3); border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background:rgba(97, 37, 123,.5); }
`;

const AddServiceModal: React.FC<AddServiceModalProps> = ({ isOpen, onClose, onServiceAdded }) => {
    const [categorias, setCategorias]         = useState<any[]>([]);
    const [sucursalesDisponibles, setSucs]    = useState<any[]>([]);
    const [formData, setFormData]             = useState({
        categoria:'', nombre:'', descripcion:'', tipo_servicio:'orden_servicio' as 'orden_servicio' | 'oficina_virtual',
        imagen: null as File|null, estado:true,
        requiere_espacio:false, sucursales:[] as number[],
        producto_servicio_sin: null as number | null, unidad_medida_sin: null as number | null
    });
    const [isLoading, setIsLoading]           = useState(false);
    const [error, setError]                   = useState<string|null>(null);

    useEffect(() => {
        if (isOpen) {
            setFormData({ categoria:'', nombre:'', descripcion:'', tipo_servicio:'orden_servicio' as 'orden_servicio' | 'oficina_virtual', imagen:null, estado:true, requiere_espacio:false, sucursales:[], producto_servicio_sin: null as number | null, unidad_medida_sin: null as number | null });
            setError(null);
            Promise.all([fetchCategories(), fetchSucursales()]).then(([cats,sucs])=>{
                setCategorias(cats.filter((c:any)=>c.estado).sort((a:any,b:any)=>a.id-b.id));
                setSucs(sucs.filter((s:any)=>s.activo===true));
            }).catch((() => undefined));
        }
    }, [isOpen]);

    const toggleSuc = (id: number) =>
        setFormData(p=>({ ...p, sucursales: p.sucursales.includes(id) ? p.sucursales.filter(x=>x!==id) : [...p.sucursales,id] }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (formData.sucursales.length===0) { setError('Debe seleccionar al menos una sucursal.'); return; }
        setIsLoading(true); setError(null);
        try {
            await createServicio({ categoria:Number(formData.categoria), nombre:formData.nombre, tipo_servicio:formData.tipo_servicio, descripcion:formData.descripcion, imagen:formData.imagen, estado:formData.estado, requiere_espacio:formData.requiere_espacio, sucursales:formData.sucursales, producto_servicio_sin:formData.producto_servicio_sin, unidad_medida_sin:formData.unidad_medida_sin });
            onServiceAdded(); onClose();
        } catch { setError('Error al crear el servicio. Por favor intente nuevamente.'); }
        finally { setIsLoading(false); }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="asm-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="asm-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>

                <LumaAccessiblePanel coworkTitle="Crear · Servicios" data-luma-dialog="true" className="asm-dialog custom-scroll card custom-card vx-card" style={{ position:'relative', background:'var(--ad-surface, #fff)', borderRadius:'24px', width:'100%', maxWidth:'600px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.18)', border:'1.5px solid var(--ad-line, #f1f5f9)' }}>

                    {/* Barra top */}
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.primary},${colors.secondary},${colors.accentDark},${colors.secondaryLight})`, borderRadius:'24px 24px 0 0' }}/>

                    {/* Header */}
                    <div style={{ padding:'24px 28px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                                <div style={{ width:'36px', height:'36px', borderRadius:'11px', background:`linear-gradient(135deg,${colors.primary},${colors.primaryDark})`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px color-mix(in srgb, ${colors.primary} 18.82%, transparent)` }}>
                                    <Plus size={18} color="#fff"/>
                                </div>
                                <div>
                                    <LumaAccessibleTitle style={{ fontSize:'1.1rem', fontWeight:'650', color:'var(--ad-ink, #0f172a)', letterSpacing:'-.02em' }}>Nuevo Servicio</LumaAccessibleTitle>
                                    <p style={{ fontSize:'.72rem', color:'var(--ad-muted, #94a3b8)', fontWeight:'500' }}>Completa los datos para crear el servicio</p>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'4px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'10px', border:'1.5px solid var(--ad-line, #f1f5f9)', background:'var(--ad-soft, #f8fafc)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='var(--ad-line, #f1f5f9)'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='var(--ad-soft, #f8fafc)'}>
                            <X size={15} color="var(--ad-muted, #64748b)"/>
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} style={{ padding:'20px 28px 28px', display:'flex', flexDirection:'column', gap:'16px' }}><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>

                        {error && (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', background:`color-mix(in srgb, ${colors.primary} 3.14%, transparent)`, border:`1.5px solid color-mix(in srgb, ${colors.primary} 13.33%, transparent)`, borderRadius:'10px', padding:'10px 14px' }}>
                                <X size={14} color={colors.primary}/>
                                <span style={{ fontSize:'.82rem', color:'var(--ad-link, #2854c5)', fontWeight:'600' }}>{error}</span>
                            </div>
                        )}

                        {/* Categoría + Nombre */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Tag size={11} color={colors.accentDark}/> Categoría *
                                </label>
                                <select required value={formData.categoria} onChange={e=>setFormData({...formData,categoria:e.target.value, producto_servicio_sin: null, unidad_medida_sin: null})} className="asm-input asm-select">
                                    <option value="">Seleccionar categoría</option>
                                    {categorias.map(c=><option key={c.id} value={c.id}>{c.nombre}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Nombre *</label>
                                <input type="text" required value={formData.nombre} onChange={e=>setFormData({...formData,nombre:e.target.value})} placeholder="Ej: Masaje Relajante" className="asm-input"/>
                            </div>
                        </div>

                        <HomologacionSINFields className="asm-input asm-select" producto={formData.producto_servicio_sin} unidad={formData.unidad_medida_sin} actividad={categorias.find(c => c.id === Number(formData.categoria))?.actividad_economica_codigo} onChange={(producto, unidad) => setFormData({ ...formData, producto_servicio_sin: producto, unidad_medida_sin: unidad })} />

                        {/* Descripción + Imagen */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Descripción</label>
                                <textarea value={formData.descripcion} onChange={e=>setFormData({...formData,descripcion:e.target.value})} placeholder="Descripción opcional..." className="asm-input asm-textarea"/>
                            </div>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Imagen</label>
                                <ImageUpload setImagen={f=>setFormData({...formData,imagen:f})}/>
                            </div>
                        </div>

                        {/* Sucursales */}
                        <div>
                            <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'10px' }}>
                                <Building2 size={11} color={colors.secondary}/> Sucursales activas
                                {formData.sucursales.length>0 && <span style={{ marginLeft:'auto', fontSize:'.68rem', fontWeight:'700', color:'var(--ad-link, #2854c5)', background:`color-mix(in srgb, ${colors.secondary} 7.06%, transparent)`, borderRadius:'100px', padding:'1px 8px' }}>{formData.sucursales.length} seleccionadas</span>}
                            </label>
                            {sucursalesDisponibles.length===0 ? (
                                <div style={{ background:'var(--ad-soft, #f8fafc)', border:'1.5px solid var(--ad-line, #f1f5f9)', borderRadius:'12px', padding:'16px', textAlign:'center', color:'var(--ad-muted, #94a3b8)', fontSize:'.82rem' }} className="card custom-card vx-card">No hay sucursales activas disponibles</div>
                            ) : (
                                <div className="custom-scroll card custom-card vx-card" style={{ background:'var(--ad-soft, #f8fafc)', border:'1.5px solid var(--ad-line, #f1f5f9)', borderRadius:'14px', padding:'12px', maxHeight:'140px', overflowY:'auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                                    {sucursalesDisponibles.map(suc=>{
                                        const checked = formData.sucursales.includes(suc.id);
                                        return (
                                            <label key={suc.id} className="asm-suc-item" style={{ display:'flex', alignItems:'center', gap:'8px', padding:'7px 10px', cursor:'pointer' }}>
                                                <div onClick={()=>toggleSuc(suc.id)} style={{ width:'18px', height:'18px', borderRadius:'5px', border:`1.5px solid ${checked?colors.secondary:'#d1d5db'}`, background:checked?colors.secondary:'#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all .15s', cursor:'pointer' }}>
                                                    {checked&&<svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                                </div>
                                                <input type="checkbox" checked={checked} onChange={()=>toggleSuc(suc.id)} style={{ display:'none' }}/>
                                                <span style={{ fontSize:'.82rem', color:'var(--ad-ink, #374151)', fontWeight:checked?'700':'500' }}>{suc.nombre} <span style={{ color:'var(--ad-muted, #94a3b8)', fontSize:'.72rem' }}>({suc.codigo})</span></span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                            <p style={{ fontSize:'.7rem', color:'var(--ad-muted, #94a3b8)', marginTop:'5px' }}>Se requiere al menos una sucursal</p>
                        </div>

                        <label>Tipo de servicio
                            <select className="asm-input" value={formData.tipo_servicio} onChange={e => setFormData(p => ({...p, tipo_servicio: e.target.value as 'orden_servicio' | 'oficina_virtual', requiere_espacio: false}))}>
                                <option value="orden_servicio">Orden de servicio</option>
                                <option value="oficina_virtual">Oficina virtual</option>
                            </select>
                        </label>
                        {/* Requiere espacio + Estado */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                            {[
                                { label:'¿Requiere espacio físico?', val:formData.requiere_espacio, key:'requiere_espacio', desc:formData.requiere_espacio?'Necesita espacio asignado':'Sin espacio específico' },
                                { label:'Estado del servicio', val:formData.estado, key:'estado', desc:formData.estado?'● Activo':'○ Inactivo' },
                            ].map(item=>(
                                <div key={item.key} style={{ background:'var(--ad-soft, #f8fafc)', border:'1.5px solid var(--ad-line, #f1f5f9)', borderRadius:'14px', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }} className="card custom-card vx-card">
                                    <div>
                                        <p style={{ fontSize:'.82rem', fontWeight:'700', color:'var(--ad-ink, #0f172a)', marginBottom:'2px' }}>{item.label}</p>
                                        <p style={{ fontSize:'.7rem', color: item.val ? colors.secondaryLight : 'var(--ad-muted, #94a3b8)', fontWeight:'600' }}>{item.desc}</p>
                                    </div>
                                    <div onClick={()=>{if(item.key === 'requiere_espacio' && formData.tipo_servicio === 'oficina_virtual') return; setFormData(p=>({...p,[item.key]:!p[item.key as keyof typeof p]}));}} style={{ width:'44px', height:'24px', borderRadius:'100px', background:item.val?colors.secondary:'#d1d5db', position:'relative', cursor:'pointer', transition:'background .25s', flexShrink:0 }}>
                                        <div style={{ position:'absolute', top:'3px', left:item.val?'23px':'3px', width:'18px', height:'18px', borderRadius:'50%', background:'var(--ad-surface, #fff)', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .25s' }}/>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', paddingTop:'4px' }}>
                            <button type="button" onClick={onClose} className="asm-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={isLoading||formData.sucursales.length===0} className="asm-btn-primary btn btn-primary">
                                {isLoading
                                    ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }}/> Guardando...</>
                                    : <><Plus size={14}/> Crear Servicio</>
                                }
                            </button>
                        </div>
                    </form>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        </>,
        document.body
    );
};

export default AddServiceModal;




